import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import { query } from '@warp-drive/utilities/json-api';
import type { Store } from '@warp-drive/core';
import type { TOC } from '@ember/component/template-only';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import Heading from 'tio-ui/components/heading';
import { Section, VStack } from 'tio-ui/components/layout';
import PageHero from 'tio-ui/components/page-hero';
import Text from 'tio-ui/components/text';
import { PhFileText } from 'ember-phosphor-icons';
import { getStatus } from 'tio-common/helpers/observability';
import DocumentsTable from 'tio-employee/components/observability/documents-table';
import { uploadIntentFor } from 'tio-employee/utils/observability/upload-intent';
import type PartnerService from 'tio-employee/services/partner';
import type { ObservableDocumentExtension as ObservableDocumentModel } from 'tio-data/data/resources/observable-document.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';

interface DocumentsLandingSignature {
  Args: {
    /** The person whose documents the page lists, with `employees.plan` loaded. */
    person: PersonModel;
  };
}

const VERIFIED = new Set(['PROCESSED', 'APPLIED', 'PARTIALLY_APPLIED']);
const ACTION_NEEDED = new Set(['INVALID', 'FAILED', 'NOT_APPLIED']);
const COUNT_LIMIT = 100;

/** A count tile inside the hero on the nested card the dashboards use. */
const CountTile: TOC<{ Args: { label: string; value: number; caption: string } }> = <template>
  <Card @radius="nested" @padding="sm" role="group" aria-label={{@label}}>
    <div class="tio-col-extra-tight">
      <Text @variant="muted" @size="xs">{{@label}}</Text>
      <div class="tio-text-2xl-bold tio-fg-primary">{{@value}}</div>
      <Text @variant="muted" @size="xs">{{@caption}}</Text>
    </div>
  </Card>
</template>;

/**
 * The documents landing a beta user reaches by visiting the upload route
 * without an upload intent: the dashboard hero with the upload action and
 * how many documents are verified, pending or need a new upload, then the
 * shipped documents table with a proper empty state.
 */
export default class DocumentsLanding extends Component<DocumentsLandingSignature> {
  @service declare partner: PartnerService;
  @service declare store: Store;

  get uploadIntent() {
    return uploadIntentFor(this.args.person, this.partner);
  }

  countsData = trackedFunction(this, async () => {
    const { content } = (await this.store.request(
      query<ObservableDocumentModel>('observable-document', {
        'filter[person]': this.args.person.id,
        'filter[discarded_at]': 'null',
        'page[limit]': COUNT_LIMIT,
      })
    )) as { content: { data: ObservableDocumentModel[] } };
    return content.data;
  });

  get counts() {
    const documents = this.countsData.value || [];
    let verified = 0;
    let actionNeeded = 0;
    for (const document of documents) {
      const status = getStatus(document);
      if (VERIFIED.has(status)) verified += 1;
      else if (ACTION_NEEDED.has(status)) actionNeeded += 1;
    }
    return { verified, actionNeeded, pending: documents.length - verified - actionNeeded };
  }

  <template>
    <VStack class="gap-8" data-test-documents-landing>
      <PageHero
        @icon={{PhFileText}}
        @title={{t "observability.documents.index.title"}}
        @subtitle={{t "observability.documents.landing.subtitle"}}
        @gradient="brand-soft"
        class="tio-elevation-card-raised"
      >
        <:actions>
          <Button
            @route="authenticated.observability.upload"
            @query={{this.uploadIntent}}
            @intent="primary"
            @appearance="outlined"
            data-test-documents-landing-upload
          >
            {{t "observability.documents.landing.upload"}}
          </Button>
        </:actions>
        <:default>
          <div class="app-card-grid app-card-grid--medium mt-6" data-test-documents-landing-counts>
            <CountTile
              @label={{t "observability.documents.landing.verified"}}
              @value={{this.counts.verified}}
              @caption={{t "observability.documents.landing.verified_caption"}}
            />
            <CountTile
              @label={{t "observability.documents.landing.pending"}}
              @value={{this.counts.pending}}
              @caption={{t "observability.documents.landing.pending_caption"}}
            />
            <CountTile
              @label={{t "observability.documents.landing.action_needed"}}
              @value={{this.counts.actionNeeded}}
              @caption={{t "observability.documents.landing.action_needed_caption"}}
            />
          </div>
        </:default>
      </PageHero>

      <Section data-test-documents-landing-zone>
        <:header>{{t "accounts.my_documents"}}</:header>
        <:body>
          <DocumentsTable @person={{@person}}>
            <:empty>
              <VStack data-test-documents-landing-empty>
                <Heading @level={{3}}>{{t "observability.documents.landing.empty_title"}}</Heading>
                <Text @variant="muted">{{t "observability.documents.landing.empty_body"}}</Text>
                <div>
                  <Button
                    @route="authenticated.observability.upload"
                    @query={{this.uploadIntent}}
                    @intent="primary"
                  >
                    {{t "observability.documents.landing.upload"}}
                  </Button>
                </div>
              </VStack>
            </:empty>
          </DocumentsTable>
        </:body>
      </Section>
    </VStack>
  </template>
}
